/**
 * Boundary normalisation.
 *
 * The API returns only what the schema has. Every `// TARGET` field in contract.ts is absent
 * from a real response today, so payloads are filled to the full contract shape here, once, on
 * the way in. Screens then render a complete object and missing values show as em dashes.
 *
 * This is not deriving state - no value is invented or computed from another. It fills absent
 * optional fields with empty, which is what the mockups show for "not evaluated yet".
 *
 * Without this, a screen built against the mock crashes on the real response the first time it
 * maps over an absent array.
 */
import { materialiseQualityBlocks } from '@platform/contracts';
import type {
  CatalogPage,
  DatasetDetail,
  DatasetPage,
  DatasetSummary,
  OperatorDashboard,
  QualityCheck,
  QualityReport,
  VendorDashboard
} from './contract';

type Raw = Record<string, unknown>;

const arr = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/** Fill a dataset row out to the full DatasetSummary shape. */
export function normalizeDataset(raw: Raw): DatasetSummary {
  return {
    // LIVE - present today
    id: String(raw.id ?? ''),
    tenant_id: String(raw.tenant_id ?? ''),
    name: String(raw.name ?? ''),
    lifecycle_status: String(raw.lifecycle_status ?? 'awaiting_upload'),
    status: String(raw.status ?? ''),
    terminal_outcome: str(raw.terminal_outcome),
    submitted_at: str(raw.submitted_at),
    created_at: String(raw.created_at ?? ''),
    vendor_name: String(raw.vendor_name ?? ''),
    package_version: num(raw.package_version) ?? 1,
    is_public: Boolean(raw.is_public),

    // TARGET - absent until the backend gains them
    hours: num(raw.hours),
    format_label: str(raw.format_label),
    intake_method: (str(raw.intake_method) as DatasetSummary['intake_method']) ?? null,
    gate: (str(raw.gate) as DatasetSummary['gate']) ?? null,
    quality_score: num(raw.quality_score),
    quality_blocks: arr(raw.quality_blocks),
    open_flag_count: num(raw.open_flag_count) ?? 0,
    modalities: arr(raw.modalities),
    collection_type: (str(raw.collection_type) as DatasetSummary['collection_type']) ?? null,
    rig: str(raw.rig),
    dataset_code: str(raw.dataset_code),
    version_label: str(raw.version_label),
    availability: (str(raw.availability) as DatasetSummary['availability']) ?? null,
    certified_at: str(raw.certified_at)
  };
}

export function normalizeDatasetPage(raw: Raw): DatasetPage {
  return {
    data: arr<Raw>(raw.data).map(normalizeDataset),
    next_cursor: str(raw.next_cursor)
  };
}

export function normalizeCatalogPage(raw: Raw): CatalogPage {
  const totals = raw.totals as Raw | undefined;
  return {
    ...normalizeDatasetPage(raw),
    // Empty rather than absent: the chips map over this on first render, before the response lands.
    filter_options: (raw.filter_options ?? {}) as CatalogPage['filter_options'],
    // Absent totals render as a plain header rather than a wrong number computed from the page.
    totals: totals
      ? {
          dataset_count: num(totals.dataset_count) ?? 0,
          in_pipeline_count: num(totals.in_pipeline_count) ?? 0,
          certified_product_count: num(totals.certified_product_count) ?? 0,
          vendor_count: num(totals.vendor_count) ?? 0,
          total_hours: num(totals.total_hours) ?? 0
        }
      : (undefined as unknown as CatalogPage['totals'])
  };
}

/** An absent report renders as five unevaluated blocks rather than crashing the screen. */
function normalizeQualityReport(raw: unknown, versionLabel: string | null): QualityReport {
  const r = (raw ?? {}) as Raw;
  const blocks = arr<{ key: string }>(r.blocks);
  return {
    version_label: str(r.version_label) ?? versionLabel ?? '',
    meta: str(r.meta) ?? '',
    gate: (str(r.gate) as QualityReport['gate']) ?? null,
    // The same rule the API applies on both its read and write paths, so a partial report and a
    // full one render the same five positions. This used to be a third hand-written copy that
    // only materialised when the server sent *nothing*, leaving a partial list partial.
    blocks: materialiseQualityBlocks(blocks).map(({ key, verdict, detail, checks }) => ({
      key,
      verdict,
      ...(detail === null ? {} : { detail }),
      // Kept, not dropped. `detail` is a count -- "3 checks - 1 fail" -- and a count cannot tell an
      // operator which check failed, which is the only thing they actually need in order to decide.
      // Shape-checked here rather than trusted: these travel from the QA vendor's own response.
      checks: (Array.isArray(checks) ? checks : []).flatMap(c => {
        const check = c as { code?: unknown; verdict?: unknown; observed?: unknown };
        return typeof check?.code === 'string'
          ? [
              {
                code: check.code,
                verdict: check.verdict as QualityCheck['verdict'],
                observed: String(check.observed ?? '')
              }
            ]
          : [];
      })
    })) as QualityReport['blocks'],
    qualifiers: (r.qualifiers as Record<string, string>) ?? {}
  };
}

export function normalizeDatasetDetail(raw: Raw): DatasetDetail {
  return {
    ...normalizeDataset(raw),
    items: arr<Raw>(raw.items).map(item => ({
      id: String(item.id ?? ''),
      // Filled to the contract shape like everything else here: a file list is mapped over the
      // moment a batch is opened, and `files` is absent on an item that has none yet.
      files: arr<Raw>(item.files).map(file => ({
        id: String(file.id ?? ''),
        original_name: String(file.original_name ?? ''),
        relative_path: str(file.relative_path),
        file_size_bytes: num(file.file_size_bytes),
        status: String(file.status ?? ''),
        blocking_reason: str(file.blocking_reason),
        blocking_summary: str(file.blocking_summary),
        qa_verdict: str(file.qa_verdict),
        qa_code: str(file.qa_code),
        qa_message: str(file.qa_message)
      }))
    })),
    black_box_run: (raw.black_box_run as DatasetDetail['black_box_run']) ?? null,
    size_bytes: num(raw.size_bytes),
    episode_count: num(raw.episode_count),
    episodes: arr(raw.episodes),
    flags: arr(raw.flags),
    lineage: arr(raw.lineage),
    evidence: arr(raw.evidence),
    delivery_refs: arr(raw.delivery_refs),
    intake_spec: str(raw.intake_spec),
    quality_report: normalizeQualityReport(raw.quality_report, str(raw.version_label)),
    decision: (raw.decision as DatasetDetail['decision']) ?? null
  };
}

export function normalizeVendorDashboard(raw: Raw): VendorDashboard {
  return {
    datasets: (raw.datasets as Record<string, number>) ?? {},
    delivered_hours: num(raw.delivered_hours),
    certified_dataset_count: num(raw.certified_dataset_count),
    in_evaluation: (raw.in_evaluation as VendorDashboard['in_evaluation']) ?? null,
    pass_rate_90d: (raw.pass_rate_90d as VendorDashboard['pass_rate_90d']) ?? null,
    open_flags: (raw.open_flags as VendorDashboard['open_flags']) ?? null,
    activity: arr(raw.activity),
    actions: arr(raw.actions)
  };
}

export function normalizeOperatorDashboard(raw: Raw): OperatorDashboard {
  return {
    tenants: num(raw.tenants) ?? 0,
    datasets: num(raw.datasets) ?? 0,
    runs: (raw.runs as Record<string, number>) ?? {},
    total_hours: (raw.total_hours as OperatorDashboard['total_hours']) ?? null,
    new_datasets_in_range: num(raw.new_datasets_in_range),
    active_vendors: (raw.active_vendors as OperatorDashboard['active_vendors']) ?? null,
    quality_pass_rate: (raw.quality_pass_rate as OperatorDashboard['quality_pass_rate']) ?? null,
    open_flags: (raw.open_flags as OperatorDashboard['open_flags']) ?? null,
    hours_by_modality: arr(raw.hours_by_modality),
    collection_share: arr(raw.collection_share),
    formats: arr(raw.formats),
    capability_matrix: arr(raw.capability_matrix) as OperatorDashboard['capability_matrix'],
    upload_activity: arr(raw.upload_activity),
    filter_options: (raw.filter_options ?? {}) as OperatorDashboard['filter_options'],
    applied: (raw.applied ?? {}) as OperatorDashboard['applied']
  };
}
